import { BrowserModule } from "@angular/platform-browser";
import { NgModule, Provider, forwardRef, APP_INITIALIZER } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { RouterModule } from "@angular/router";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AgGridModule } from "ag-grid-angular";
import { ClipboardModule } from "@angular/cdk/clipboard";

import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatDialogModule } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatRadioModule } from "@angular/material/radio";
import { MatTabsModule } from "@angular/material/tabs";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatCardModule } from "@angular/material/card";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatMenuModule } from "@angular/material/menu";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatTreeModule } from "@angular/material/tree";
import { MatIconModule } from "@angular/material/icon";

import { NgxPayPalModule } from "ngx-paypal";

import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

import { AppComponent } from "./app.component";
import { NavMenuComponent } from "./tools/nav-menu/nav-menu.component";

import { HomeComponent } from "./modules/home/home.component";
import { AccountRegisterComponent } from "./modules/account/register/account-register.component";
import { AccountLoginComponent } from "./modules/account/login/account-login.component";
import { PowerBIEmbeddedComponent } from "./modules/powerbi/embedded/powerbi-embedded.component";
import { InteractiveComponent } from "./modules/interactive/interactive.component";
import { SubscriptionComponent } from "./modules/subscription/subscription.component";
import { SubscriptionManagementComponent } from "./modules/subscription/subscriptionmanagement.component";
import { AdminComponent } from "./modules/admin/admin.component";
import { PurchaseSubscriptionComponent } from "./modules/purchase-subscription/purchase-subscription.component";
import { UpdateSubscriptionComponent } from "./modules/update-subscription/update-subscription.component";
import { ResetPasswordComponent } from "./modules/account/reset-password/reset-password.component";
import { ConfirmEmailComponent } from "./modules/account/confirm-email/confirm-email.component";
import { ReportExportsComponent } from "./modules/report-exports/report-exports.component";

import { CounterComponent } from "./modules/counter/counter.component";
import { FetchDataComponent } from "./modules/fetch-data/fetch-data.component";
import { ApiInterceptor } from "./tools/intercepters/ApiIntercepter";
import { SafePipe } from "./tools/transforms/safepipe";

import { AppInitService } from "./tools/app-init.service";

import { CopyInteractiveDialogComponent } from "./dialogs/copy-interactive-dialog/copy-interactive-dialog.component";
import { ConfirmDialogComponent } from "./dialogs/confirm-dialog/confirm-dialog.component";
import { MessageDialogComponent } from "./dialogs/message-dialog/message-dialog.component";
import { HtmlMessageDialogComponent } from "./dialogs/html-message-dialog/html-message-dialog.component";
import { TextEditDialogComponent } from "./dialogs/text-edit-dialog/text-edit-dialog.component";
import { InputEditDialogComponent } from "./dialogs/input-edit-dialog/input-edit-dialog.component";
import { DropDownSelectDialogComponent } from "./dialogs/dropdown-select-dialog/dropdown-select-dialog.component";
import { ShareInteractiveDialogComponent } from "./dialogs/share-interactive-dialog/share-interactive-dialog.component";
import { LoginDialogComponent } from "./dialogs/login-dialog/login-dialog.component";
import { RegisterDialogComponent } from "./dialogs/register-dialog/register-dialog.component";
import { AddSubscriptionUserDialogComponent } from "./dialogs/add-subscription-user-dialog/add-subscription-user-dialog.component";
import { InteractMenuDialogComponent } from "./dialogs/interact-menu-dialog/interact-menu-dialog.component";

import { EditInteractiveDialogComponent } from "./dialogs/admin/edit-interactive-dialog/edit-interactive-dialog.component";
import { EditInteractiveSubscriptionsDialogComponent } from "./dialogs/admin/edit-interactive-subscription-dialog/edit-interactive-subscription-dialog.component";
import { EditSubLevelsDialogComponent } from "./dialogs/admin/edit-subscription-level-dialog/edit-subscription-level-dialog.component";
import { EditSubPricesDialogComponent } from "./dialogs/admin/edit-subscription-price-dialog/edit-subscription-price-dialog.component";
import { EditUnpaidOrderDialogComponent } from "./dialogs/admin/edit-unpaid-order-dialog/edit-unpaid-order-dialog.component";
import { EditSubscriptionDialogComponent } from "./dialogs/admin/edit-subscription-dialog/edit-subscription-dialog.component";
import { EditCouponsDialogComponent } from "./dialogs/admin/edit-coupon-dialog/edit-coupon-dialog.component";
import { EditCouponLevelDialogComponent } from "./dialogs/admin/edit-coupon-level-dialog/edit-coupon-level-dialog.component";
import { EditCouponLevelsDialogComponent } from "./dialogs/admin/edit-coupon-levels-dialog/edit-coupon-levels-dialog.component";
import { EditXmlaSubscriptionsDialogComponent } from "./dialogs/admin/edit-xmla-subscription-dialog/edit-xmla-subscription-dialog.component";
import { EditXmlaSubscriptionLevelsDialogComponent } from "./dialogs/admin/edit-xmla-subscription-level-dialog/edit-xmla-subscription-level-dialog.component";
import { EditXmlaSubscriptionPricesDialogComponent } from "./dialogs/admin/edit-xmla-subscription-price-dialog/edit-xmla-subscription-price-dialog.component";
import { EditInteractiveFilterDialogComponent } from "./dialogs/edit-interactive-filter-dialog/edit-interactive-filter-dialog.component";
import { EditInteractiveBookmarkDialogComponent } from "./dialogs/edit-interactive-bookmark-dialog/edit-interactive-bookmark-dialog.component";
import { EditUserDialogComponent } from "./dialogs/admin/edit-user-dialog/edit-user-dialog.component";
import { EditInteractReportsDialogComponent } from "./dialogs/admin/edit-interactive-report-dialog/edit-interactive-report-dialog.component";
import { ReportExportDialogComponent } from "./dialogs/report-export-dialog/report-export-dialog.component";
import { EditInteractivePreFiltersDialogComponent } from './dialogs/admin/edit-interactive-pre-filters-dialog/edit-interactive-pre-filters-dialog.component';
import { EditInteractivePreFilterComponent } from './modules/subscription/edit-interactive-pre-filter/edit-interactive-pre-filter.component';
import { EditPartnerEmbedDialogComponent } from './dialogs/admin/edit-partner-embed-dialog/edit-partner-embed-dialog.component';

import { PowerBiReportExportStart } from './modules/powerbi/report-export/powerbi-report-export-start.component';
import { PowerBiReportExport } from './modules/powerbi/report-export/powerbi-report-export.component';

import { SimpleGridComponent } from "./elements/simple-grid/simple-grid.component";
import { ShareEditorComponent } from "./elements/share-editor/share-editor.component";
import { TreeEditorComponent } from "./elements/tree-editor/tree-editor.component";
import { PreFilterSelectionComponent } from "./elements/pre-filter-selection/pre-filter-selection.component";

import { UserTypeFilterPipe } from "./pipes/UserType.Pipe";

import { PowerbiEmbedComponent } from './elements/powerbi-embed/powerbi-embed.component';
import { CommonModule } from "@angular/common";
import { PartnerEmbedComponent } from './modules/powerbi/partner-embed/partner-embed.component';
import { PreFilterHierarchyEditorComponent } from './elements/pre-filter-hierarchy-editor/pre-filter-hierarchy-editor.component';

import { PreFilterEditorComponent } from './elements/pre-filter-editor/pre-filter-editor.component';
import { PreFilterIndustryDialogComponent } from './elements/pre-filter-editor/dialogs/pre-filter-industry-dialog/pre-filter-industry-dialog.component';
import { PreFilterOccupationDialogComponent } from './elements/pre-filter-editor/dialogs/pre-filter-occupation-dialog/pre-filter-occupation-dialog.component';
import { PreFilterRegionDialogComponent } from './elements/pre-filter-editor/dialogs/pre-filter-region-dialog/pre-filter-region-dialog.component';
import { PreFilterTextDialogComponent } from './elements/pre-filter-editor/dialogs/pre-filter-text-dialog/pre-filter-text-dialog.component';
import { PreFilterEditorDialogComponent } from "./dialogs/pre-filter-editor-dialog/pre-filter-editor-dialog.component";


import { TestPageComponent } from "./modules/test-page/test-page.component";

@NgModule({
  exports: [
    MatSnackBarModule,
    MatSelectModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatTabsModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCardModule,
    MatExpansionModule,
    MatMenuModule,
    MatAutocompleteModule,
    MatTreeModule,
    MatIconModule,

    NgxMatSelectSearchModule,
  ],
  imports: [
    CommonModule
  ],
  declarations: []
})
export class MaterialModule {}

export function initializeApp(appInitService: AppInitService) {
  return (): Promise<any> => {
    return appInitService.Init();
  };
}

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true,
};

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    HomeComponent,

    SafePipe,

    AccountRegisterComponent,
    AccountLoginComponent,
    PowerBIEmbeddedComponent,
    InteractiveComponent,
    AdminComponent,
    SubscriptionComponent,
    SubscriptionManagementComponent,
    PurchaseSubscriptionComponent,
    UpdateSubscriptionComponent,
    ResetPasswordComponent,
    ConfirmEmailComponent,
    ReportExportsComponent,

    CopyInteractiveDialogComponent,
    ConfirmDialogComponent,
    MessageDialogComponent,
    HtmlMessageDialogComponent,
    TextEditDialogComponent,
    InputEditDialogComponent,
    DropDownSelectDialogComponent,
    ShareInteractiveDialogComponent,
    LoginDialogComponent,
    RegisterDialogComponent,
    AddSubscriptionUserDialogComponent,
    InteractMenuDialogComponent,

    EditInteractiveDialogComponent,
    EditInteractiveSubscriptionsDialogComponent,
    EditSubLevelsDialogComponent,
    EditSubPricesDialogComponent,
    EditCouponsDialogComponent,
    EditCouponLevelDialogComponent,
    EditCouponLevelsDialogComponent,
    EditUnpaidOrderDialogComponent,
    EditSubscriptionDialogComponent,
    EditXmlaSubscriptionsDialogComponent,
    EditXmlaSubscriptionLevelsDialogComponent,
    EditXmlaSubscriptionPricesDialogComponent,
    EditInteractiveFilterDialogComponent,
    EditInteractiveBookmarkDialogComponent,
    EditUserDialogComponent,
    EditInteractReportsDialogComponent,
    ReportExportDialogComponent,
    EditInteractivePreFiltersDialogComponent,
    EditInteractivePreFilterComponent,
    EditPartnerEmbedDialogComponent,

    PowerBiReportExportStart,
    PowerBiReportExport,

    SimpleGridComponent,
    ShareEditorComponent,
    TreeEditorComponent,
    PreFilterSelectionComponent,

    CounterComponent,
    FetchDataComponent,

    UserTypeFilterPipe,

    PowerbiEmbedComponent,
    PartnerEmbedComponent,

    PreFilterHierarchyEditorComponent,
    PreFilterEditorDialogComponent,

    PreFilterEditorComponent,
    PreFilterIndustryDialogComponent,
    PreFilterOccupationDialogComponent,
    PreFilterRegionDialogComponent,
    PreFilterTextDialogComponent,

    TestPageComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: "ng-cli-universal" }),
    BrowserAnimationsModule,
    HttpClientModule,

    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    ClipboardModule,

    NgxPayPalModule,

    AgGridModule,

    RouterModule.forRoot([
      { path: "", component: HomeComponent, pathMatch: "full" },
      { path: "register", component: AccountRegisterComponent },
      { path: "login", component: AccountLoginComponent },
      { path: "interactives", component: InteractiveComponent },
      { path: "admin", component: AdminComponent },
      {
        path:
          "powerbi-embedded/:interactId/:interactType/:actionType/:filterId/:bookmarkId/:reportId",
        component: PowerBIEmbeddedComponent,
      },
      { path: "subscription", component: SubscriptionComponent },
      {
        path: "subscriptionmanagement/:subscriptionId",
        component: SubscriptionManagementComponent,
      },
      {
        path:
          "subscription-purchase/:packagetype/:subscriptiontype/:subscriptionlevel",
        component: PurchaseSubscriptionComponent,
      },
      {
        path:
          "subscription-purchase/:packagetype/:subscriptiontype/:subscriptionlevel/:couponCode",
        component: PurchaseSubscriptionComponent,
      },
      {
        path: "update-subscription/:subscriptionId/:updateType",
        component: UpdateSubscriptionComponent,
      },
      { path: "resetpassword/:resetToken", component: ResetPasswordComponent },
      {
        path: "confirmemail/:emailAddress/:confirmationCode",
        component: ConfirmEmailComponent,
      },
      {
        path: "edit-interactive-prefilter/:interactiveId/:interactivePreFilterId",
        component: EditInteractivePreFilterComponent
      },
      { path: "report-exports", component: ReportExportsComponent },
      { path: "report-export-internal-start/:reportInteractiveId/:reportExportId/:reportPage", component: PowerBiReportExportStart },
      { path: "report-export-internal-process/:reportInteractiveId/:reportExportId/:reportPage", component: PowerBiReportExport },

      { path: "counter", component: CounterComponent },
      { path: "fetch-data", component: FetchDataComponent },
      { path: "partner-embed/:partnerApiKey", component: PartnerEmbedComponent },
      { path: "test-page", component: TestPageComponent }
    ]),
  ],

  providers: [
    ApiInterceptor,
    API_INTERCEPTOR_PROVIDER,
    AppInitService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AppInitService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  /*entryComponents: [
    CopyInteractiveDialogComponent,
    ConfirmDialogComponent,
    MessageDialogComponent,
    HtmlMessageDialogComponent,
    TextEditDialogComponent,
    InputEditDialogComponent,
    ShareInteractiveDialogComponent,
    LoginDialogComponent,
    RegisterDialogComponent,
    AddSubscriptionUserDialogComponent,
    InteractMenuDialogComponent,

    PreFilterEditorDialogComponent,
    PreFilterSelectionComponent,

    EditInteractiveDialogComponent,
    EditInteractiveSubscriptionsDialogComponent,
    EditSubLevelsDialogComponent,
    EditSubPricesDialogComponent,
    EditCouponsDialogComponent,
    EditCouponLevelDialogComponent,
    EditCouponLevelsDialogComponent,
    EditSubPricesDialogComponent,
    EditUnpaidOrderDialogComponent,
    EditSubscriptionDialogComponent,
    EditXmlaSubscriptionsDialogComponent,
    EditXmlaSubscriptionLevelsDialogComponent,
    EditXmlaSubscriptionPricesDialogComponent,
    EditInteractiveFilterDialogComponent,
    EditInteractiveBookmarkDialogComponent,
    EditUserDialogComponent,
    EditInteractReportsDialogComponent,
    ReportExportDialogComponent,
  ],*/
})
export class AppModule {}
